"use client";

import { router } from "@inertiajs/react";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";

import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDataTable } from "@/hooks/use-data-table";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import { organizationPricesPath } from "@/routes";
import type { PagyMetadata } from "@/types";

import { type PriceHistoryRecord, buildColumns } from "./columns";

interface PricesDataTableProps {
  prices: PriceHistoryRecord[];
  pagy: PagyMetadata;
  filters: Record<string, string | number | undefined>;
  organizationSlug: string;
}

export function PricesDataTable({
  prices,
  pagy,
  filters,
  organizationSlug,
}: PricesDataTableProps) {
  const { isLoading, handlePageChange, handleFilterChange } = useDataTable({
    filters,
    pagy,
    path: organizationPricesPath(organizationSlug),
  });
  const columns = buildColumns(organizationSlug);

  // Search filter for product name or code
  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearch,
  } = useSyncedDebouncedValue((filters.search as string) ?? "");

  // Apply search filter when debounced value changes
  useEffect(() => {
    if ((filters.search ?? "") !== debouncedSearch) {
      handleFilterChange({ search: debouncedSearch || undefined });
    }
  }, [debouncedSearch]);

  const clearFilters = () => {
    setSearchValue("");
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* Search Filter */}
      <div className="flex items-center gap-4">
        <Input
          placeholder="Buscar por nombre o código de producto..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="max-w-md"
        />

        {searchValue && (
          <Button variant="outline" onClick={clearFilters}>
            <Trash2 className="h-4 w-4" /> Limpiar filtros
          </Button>
        )}
      </div>

      {/* DataTable */}
      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns}
          data={prices}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false}
        />
      </div>

      {/* Server-side pagination */}
      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="precios"
      />
    </div>
  );
}
